import { create } from 'zustand';
import { CHECKOUT_STEPS, DEFAULT_SELECTION } from 'lib/billing/config';
import { PAYMENT_TAB, type CheckoutActions, type CheckoutForm, type CheckoutState, type Selection, type SelectionPatch } from './types';

const EMPTY_FORM: CheckoutForm = { email: '', firstName: '', lastName: '' };

const INITIAL_STATE: CheckoutState = {
  selection: DEFAULT_SELECTION as Selection,
  step: CHECKOUT_STEPS.configure,
  form: EMPTY_FORM,
  submitting: false,
  checkoutSession: null,
  drawerOpen: false,
  // Stripe coupon NAME typed by the customer (e.g. SAVE30). Empty = none.
  coupon: '',
  paymentTab: PAYMENT_TAB.CARD,
  // Server verdict for `coupon` (useCouponValidationSync). null = unverified.
  couponInfo: null,
  onComplete: null,
  completedAt: null,
  requestedStep: null,
  pendingNewEnvKind: null,
  overdueInvoice: null,
  analyzerFocusToken: 0,
  pendingQuickPatch: null
};

function applyPatch(prev: Selection, patch: SelectionPatch): Selection {
  const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
  const sameAll = Object.keys(next).every((k) => (next as Record<string, unknown>)[k] === (prev as Record<string, unknown>)[k]);
  return sameAll ? prev : next;
}

type Store = CheckoutState & CheckoutActions;

// In-memory only. Persisting to sessionStorage caused stale carts to linger
// across refresh/cancel — hydration from active subs is the source of truth.
export const useCheckoutStore = create<Store>()((set) => ({
  ...INITIAL_STATE,

  setSelection: (patch) => set((s) => ({ selection: applyPatch(s.selection, patch) })),

  setForm: (patch) => set((s) => ({ form: { ...s.form, ...patch } })),

  setStep: (step) => set({ step }),

  // Leaving Payment ends invoice-pay mode: Configure is plan mode, not invoice
  // mode. Re-enter overdue settlement via the banner's "Pay now".
  goToConfigure: () => set({ step: CHECKOUT_STEPS.configure, checkoutSession: null, overdueInvoice: null, paymentTab: PAYMENT_TAB.CARD }),

  goToPayment: () => set({ step: CHECKOUT_STEPS.payment }),

  setSubmitting: (submitting) => set({ submitting }),

  setCheckoutSession: (checkoutSession) => set({ checkoutSession }),

  // Any code change (apply, remove, drawer-open blank) invalidates the previous
  // verification — useCouponValidationSync re-verifies the new code and writes
  // couponInfo back. Clearing here (not just in reset()) guarantees a stale
  // verdict can never describe a different code.
  setCoupon: (coupon) => set({ coupon, couponInfo: null }),

  setPaymentTab: (paymentTab) => set({ paymentTab }),

  setCouponInfo: (couponInfo) => set({ couponInfo }),

  openDrawer: (step) =>
    set((s) => ({
      drawerOpen: true,
      // A new session has not completed anything yet.
      completedAt: null,
      step: step ?? s.step,
      // Remember an explicitly requested entry step so the drawer's open-effect
      // honors it (one-shot) instead of resetting to Configure.
      requestedStep: step ?? null
    })),

  // Closing ends the invoice-pay session too — a stale overdue invoice must not
  // leak into the next open (which re-hydrates from active subs, not an invoice).
  // The one-shot requested entry step is likewise cleared.
  closeDrawer: () => set({ drawerOpen: false, overdueInvoice: null, requestedStep: null, paymentTab: PAYMENT_TAB.CARD }),

  resetSelection: () => set({ selection: DEFAULT_SELECTION as Selection }),

  setPendingNewEnvKind: (pendingNewEnvKind) => set({ pendingNewEnvKind }),

  requestAnalyzerFocus: () => set((s) => ({ analyzerFocusToken: s.analyzerFocusToken + 1 })),
  setPendingQuickPatch: (pendingQuickPatch) => set({ pendingQuickPatch }),

  setOnComplete: (onComplete) => set({ onComplete }),

  markCompleted: () => set({ completedAt: Date.now() }),

  setOverdueInvoice: (overdueInvoice) => set({ overdueInvoice }),

  // Invoice-pay mode entry: seed the overdue invoice + refresh hook and open
  // the drawer straight on Payment — there's no plan to configure, Card/Crypto
  // are the only choices. requestedStep makes the drawer's open-effect honor it.
  openOverdueDrawer: (overdueInvoice, onComplete) =>
    set({
      overdueInvoice,
      onComplete,
      completedAt: null,
      drawerOpen: true,
      step: CHECKOUT_STEPS.payment,
      requestedStep: CHECKOUT_STEPS.payment
    }),

  reset: () => set(INITIAL_STATE)
}));
